import { getProperties } from '@ember/object';
import { schedule } from '@ember/runloop';

export const noop = () => {};
export const identity = v => v;

export function isFn(value) {
  // Support: Chrome <=57, Firefox <=52
  // In some browsers, typeof returns "function" for HTML <object> elements
  // (i.e., `typeof document.createElement( "object" ) === "function"`).
  // We don't want to classify *any* DOM node as a function.
  return typeof value === 'function' && typeof value.nodeType !== 'number';
}

export function call(fn, defaultValue, ...args) {
  return isFn(fn) ? fn.call(this, ...args)
    : isFn(defaultValue) ? defaultValue.call(this, ...args) : defaultValue;
}

export function fns(...fns) {
  return fns.map(fn => {
    let defaultValue;
    let args = [];
    if (Array.isArray(fn)) {
      [ fn, defaultValue, ...args ] = fn;
    }
    return typeof fn !== 'function' ? () => defaultValue
      : (...moreArgs) => fn(...args, ...moreArgs);
  });
}

export function callLater(fn, defaultValue, ...args) {
  schedule('actions', () => {
    call.call(this, fn, defaultValue, ...args);
  });
}

export function method(obj, fn, defaultValue, ...args) {
  fn = typeof fn === 'string' && obj ? obj[fn] : fn;
  return call.call(obj, fn, defaultValue, ...args);
}

export function callComputed(obj, fns, defaultValue, ...args) {
  if (!obj || !fns) {
    return;
  }
  const singleKey = typeof fns === 'string' && fns;
  const results = {};
  fns = [].concat(fns || []);
  const cps = getProperties(obj, ...fns);
  fns.forEach(fnName => {
    const fn = isFn(cps[fnName]) ? cps[fnName]
      : isFn(defaultValue) ? defaultValue : () => defaultValue;
    results[fnName] = fn(...args);
  });
  return singleKey ? results[singleKey] : results;
}

export function curry(fn, ...args) {
  return !(fn = [].concat(fn || []).find(fn => isFn(fn))) ? () => {}
    : function(...rest) { return fn(...args, ...rest); };
}

export function wrap(fn, ...args) {
  return !(fn = [].concat(fn || []).find(fn => isFn(fn))) ? () => {}
    : !args.length ? fn : curry(fn, ...args);
}

export function pipe(...fns) {
  return initial => fns.reduce((result, fn) => fn(result), initial);
}
